import React, { useState } from "react";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Shield, Warehouse, Users, ShoppingBag, ShoppingBasket, ChevronRight, ArrowLeft } from "lucide-react";
import { canPurchase } from "../purchases/shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Logo } from "../components/Logo";
import { API } from "../utils/format";
import { useLang } from "../context/LangContext";

const ROLES = {
  purchasing: {
    label: "Acquisti", sub: "Fornitori, ordini condivisi, prezzi e arrivi", icon: ShoppingBasket,
    accent: "#8A1C2A", bg: "from-stone-50 via-white to-stone-100", cardBg: "bg-white border-stone-200",
    cardText: "text-stone-900", inputCls: "bg-white border-stone-300 text-stone-900",
    btnCls: "bg-[#8A1C2A] hover:bg-[#6B1420] text-white", accentText: "text-[#8A1C2A]",
    hero: "Portale Acquisti", heroSub: "Accesso al gruppo acquisti", decorClass: "bg-[#8A1C2A]/10",
  },
  admin: {
    label: "Amministrazione",
    sub: "Gestione completa, BI, listini",
    icon: Shield,
    accent: "#1F2937",
    bg: "from-slate-900 via-stone-900 to-zinc-900",
    cardBg: "bg-stone-900/80 border-stone-700",
    cardText: "text-white",
    inputCls: "bg-stone-800/80 border-stone-600 text-white placeholder:text-stone-500",
    btnCls: "bg-[#C5A059] hover:bg-[#B08D4A] text-stone-900 font-semibold",
    accentText: "text-[#C5A059]",
    hero: "Pannello Amministratore",
    heroSub: "Accesso riservato al management",
    decorClass: "bg-[#C5A059]/10",
  },
  warehouse: {
    label: "Magazzino",
    sub: "Pesi reali, spedizioni e logistica",
    icon: Warehouse,
    accent: "#92400E",
    bg: "from-amber-50 via-stone-50 to-amber-100",
    cardBg: "bg-white border-amber-200",
    cardText: "text-stone-900",
    inputCls: "bg-white border-amber-300 text-stone-900",
    btnCls: "bg-amber-800 hover:bg-amber-900 text-white",
    accentText: "text-amber-800",
    hero: "Portale Magazzino",
    heroSub: "Lavorazione ordini e logistica",
    decorClass: "bg-amber-300/30",
  },
  agent: {
    label: "Agenti",
    sub: "Catalogo, clienti, ordini, provvigioni",
    icon: Users,
    accent: "#8A1C2A",
    bg: "from-stone-50 via-white to-stone-100",
    cardBg: "bg-white border-stone-200",
    cardText: "text-stone-900",
    inputCls: "bg-white border-stone-300 text-stone-900",
    btnCls: "bg-[#8A1C2A] hover:bg-[#6B1420] text-white",
    accentText: "text-[#8A1C2A]",
    hero: "Portale Agente",
    heroSub: "Vendite e gestione clienti",
    decorClass: "bg-[#8A1C2A]/10",
  },
  customer: {
    label: "Clienti",
    sub: "Ordina online, catalogo, offerte",
    icon: ShoppingBag,
    accent: "#0F766E",
    bg: "from-emerald-50 via-white to-stone-50",
    cardBg: "bg-white border-emerald-200",
    cardText: "text-stone-900",
    inputCls: "bg-white border-emerald-300 text-stone-900",
    btnCls: "bg-emerald-700 hover:bg-emerald-800 text-white",
    accentText: "text-emerald-700",
    hero: "Area Clienti",
    heroSub: "Ordina, sfoglia il catalogo, scopri le offerte",
    decorClass: "bg-emerald-300/30",
  },
  superadmin: {
    label: "Super-Admin",
    sub: "Cross-tenant: gestisce tutte le aziende",
    icon: Shield,
    accent: "#C5A059",
    bg: "from-zinc-950 via-stone-900 to-yellow-900/40",
    cardBg: "bg-stone-900/90 border-[#C5A059]/40",
    cardText: "text-white",
    inputCls: "bg-stone-800 border-[#C5A059]/30 text-white placeholder:text-stone-500",
    btnCls: "bg-[#C5A059] hover:bg-[#B08D4A] text-stone-900 font-semibold",
    accentText: "text-[#C5A059]",
    hero: "Super-Admin Globale",
    heroSub: "Switching tra aziende, monitoraggio cross-tenant",
    decorClass: "bg-[#C5A059]/15",
  },
};

// Prefissi URL per i login separati per azienda (es. /it/login, /pl/login)
const TENANT_PREFIX = { "giotto-italia": "it", "giotto-polska": "pl" };
const TENANT_LABEL = { "giotto-italia": "🇮🇹 Giotto Carni Italia", "giotto-polska": "🇵🇱 Giotto Mięsa Polska" };
// Azienda predefinita per il login pubblico (multi-azienda nascosto per ora)
const DEFAULT_COMPANY_ID = "giotto-italia";

// Se l'utente è già autenticato (token valido) reindirizza subito al suo portale
const useAutoRedirectIfAuthed = (requestedRole) => {
  React.useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        const role = r.data?.role;
        const home = requestedRole === "purchasing" && canPurchase(r.data) ? "/purchases/suppliers" : ({ admin: "/admin", superadmin: "/admin", agent: "/agent/dashboard", warehouse: "/warehouse/orders", customer: "/customer/orders", purchasing: "/purchases/suppliers" })[role];
        if (home) window.location.replace(home);
      })
      .catch(() => { /* token scaduto → resta sul login */ });
  }, [requestedRole]);
};

// ============ ROLE-SELECTION LANDING (all roles) ============
// Accepts optional `tenantHint` (company preselezionata) e `basePath`
// (es. "/it/login") per i login separati per azienda.
// SuperAdmin link is NEVER shown here (it's behind a separate, undocumented URL /super-access).
export const LoginChooser = ({ tenantHint, basePath }) => {
  const navigate = useNavigate();
  useAutoRedirectIfAuthed();
  // Tutti gli accessi disponibili da /login: Agente, Cliente, Amministrazione, Magazzino
  const allRoles = ["agent", "customer", "admin", "warehouse", "purchasing"];
  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-100 via-white to-stone-100 flex flex-col items-center justify-center p-4">
      <div className="text-center mb-10">
        <Logo className="h-16 mx-auto mb-4" />
        <h1 className="text-3xl sm:text-4xl font-display text-stone-900">Area Riservata</h1>
        {tenantHint && (
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 bg-[#8A1C2A]/10 border border-[#8A1C2A]/20 rounded-full text-xs font-semibold text-[#8A1C2A]" data-testid="tenant-hint-badge">
            {TENANT_LABEL[tenantHint] || tenantHint}
          </div>
        )}
        <p className="text-stone-500 mt-2 text-sm">Scegli il tipo di accesso</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl w-full">
        {allRoles.map((key) => {
          const r = ROLES[key];
          const Icon = r.icon;
          return (
            <button
              key={key}
              onClick={() => navigate(`${basePath || "/login"}/${key}`)}
              className="group relative overflow-hidden bg-white border-2 border-stone-200 rounded-2xl p-6 text-left hover:border-[#8A1C2A] hover:shadow-xl transition-all flex items-start gap-4"
              data-testid={`login-chooser-${key}`}
            >
              <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${r.accent}15` }}>
                <Icon className="w-7 h-7" style={{ color: r.accent }} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-lg text-stone-900">{r.label}</h3>
                <p className="text-sm text-stone-500 mt-0.5">{r.sub}</p>
              </div>
              <ChevronRight className="w-5 h-5 text-stone-400 group-hover:text-[#8A1C2A] group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
            </button>
          );
        })}
      </div>
      <p className="text-xs text-stone-400 mt-8">Giotto Carni · Gestionale interno</p>
    </div>
  );
};

// ============ ROLE-THEMED LOGIN FORM ============
// `lockedCompanyId`: quando il login arriva da un URL aziendale (/it/login/agent)
// l'azienda è fissata e il selettore non viene mostrato.
export const RoleLogin = ({ role, lockedCompanyId }) => {
  const { t } = useLang();
  const cfg = ROLES[role];
  const [email, setEmail] = useState(() => localStorage.getItem(`saved_email_${role}`) || "");
  const [password, setPassword] = useState("");
  const [companyId, setCompanyId] = useState(lockedCompanyId || "");
  const [companies, setCompanies] = useState([]);
  const [rememberMe, setRememberMe] = useState(() => localStorage.getItem("remember_me_pref") !== "0");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  useAutoRedirectIfAuthed(role);

  // Load companies on mount (public endpoint — no auth required)
  // Multi-azienda NASCOSTO per ora: dal login pubblico si accede solo all'azienda
  // principale (Italia). Il tenant PL resta raggiungibile solo via /pl/login.
  React.useEffect(() => {
    axios.get(`${API}/companies/public`).then(r => {
      setCompanies(r.data || []);
      if (lockedCompanyId) { setCompanyId(lockedCompanyId); return; }
      setCompanyId(DEFAULT_COMPANY_ID);
    }).catch(() => {});
  }, [lockedCompanyId]);

  if (!cfg) return <Navigate to="/login" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setErrorMsg("");
    try {
      const r = await axios.post(`${API}/auth/login`, { email, password, company_id: companyId || undefined, remember_me: rememberMe });
      const { token, user } = r.data;
      // Superadmin role check is special — accept it on /login/super
      const acceptableRole = (role === "superadmin") ? "superadmin" : role;
      if (role === "purchasing" ? !canPurchase(user) : user.role !== acceptableRole) {
        setErrorMsg(`Questo account è di tipo "${user.role}" — usa il login corretto`);
        setLoading(false);
        return;
      }
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem("remember_me_pref", rememberMe ? "1" : "0");
      if (rememberMe) localStorage.setItem(`saved_email_${role}`, email);
      else localStorage.removeItem(`saved_email_${role}`);
      if (user.company_id) localStorage.setItem("preferred_company_id", user.company_id);
      // Superadmin: persist the list of available companies for the switcher
      if (user.role === "superadmin" && r.data.available_companies) {
        localStorage.setItem("available_companies", JSON.stringify(r.data.available_companies));
      }
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      toast.success("Accesso effettuato");
      const home = ({ admin: "/admin", agent: "/agent/dashboard", warehouse: "/warehouse/orders", customer: "/customer/orders", superadmin: "/admin", purchasing: "/purchases/suppliers" })[role];
      // Force full reload to re-initialize React tree with new user
      window.location.replace(home);
    } catch (err) {
      setErrorMsg(err?.response?.data?.detail || "Credenziali non valide");
    } finally { setLoading(false); }
  };

  const Icon = cfg.icon;
  const isDark = role === "admin";
  const lockedCompany = lockedCompanyId ? companies.find(c => c.id === lockedCompanyId) : null;
  const backPath = lockedCompanyId && TENANT_PREFIX[lockedCompanyId] ? `/${TENANT_PREFIX[lockedCompanyId]}/login` : "/login";

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 bg-gradient-to-br ${cfg.bg} relative overflow-hidden`}>
      {/* Decorative blobs */}
      <div className={`absolute top-0 left-0 w-96 h-96 rounded-full blur-3xl -translate-x-1/3 -translate-y-1/3 ${cfg.decorClass}`} />
      <div className={`absolute bottom-0 right-0 w-96 h-96 rounded-full blur-3xl translate-x-1/3 translate-y-1/3 ${cfg.decorClass}`} />

      <div className="relative z-10 w-full max-w-md">
        {/* All roles can return to the unified chooser */}
        <button
          onClick={() => navigate(backPath)}
          className={`flex items-center gap-1 text-sm mb-4 ${isDark ? "text-stone-300 hover:text-white" : "text-stone-500 hover:text-stone-900"}`}
          data-testid="login-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> {t.back}
        </button>

        <div className={`${cfg.cardBg} ${cfg.cardText} border-2 rounded-2xl shadow-2xl p-8`} data-testid={`login-card-${role}`}>
          {/* Header */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-3" style={{ backgroundColor: `${cfg.accent}20` }}>
              <Icon className="w-8 h-8" style={{ color: cfg.accent }} />
            </div>
            <h1 className={`text-2xl font-display font-semibold ${isDark ? "text-white" : "text-stone-900"}`}>{cfg.hero}</h1>
            <p className={`text-sm mt-1 ${isDark ? "text-stone-400" : "text-stone-500"}`}>{cfg.heroSub}</p>
            {lockedCompanyId && (
              <div className={`mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${isDark ? "bg-stone-800 border-stone-600 text-stone-200" : "bg-stone-100 border-stone-200 text-stone-700"}`} data-testid="login-locked-company">
                {TENANT_LABEL[lockedCompanyId] || lockedCompany?.name || lockedCompanyId}
              </div>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Selettore azienda nascosto (multi-tenant non visibile dal login pubblico) */}
            <div className="space-y-1.5">
              <Label className={isDark ? "text-stone-300" : "text-stone-700"}>{t.email}</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.emailPh}
                className={`h-11 ${cfg.inputCls}`}
                data-testid={`login-${role}-email`}
                required
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label className={isDark ? "text-stone-300" : "text-stone-700"}>{t.password}</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className={`h-11 ${cfg.inputCls}`}
                data-testid={`login-${role}-password`}
                required
              />
            </div>
            <label className={`flex items-center gap-2 text-sm cursor-pointer select-none ${isDark ? "text-stone-300" : "text-stone-600"}`}>
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 accent-[#8A1C2A]"
                data-testid={`login-${role}-remember`}
              />
              Ricordami su questo dispositivo (60 giorni)
            </label>
            {errorMsg && (
              <div className="text-xs bg-red-50 text-red-700 border border-red-200 rounded p-2" data-testid={`login-${role}-error`}>
                {errorMsg}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              className={`w-full h-11 ${cfg.btnCls}`}
              data-testid={`login-${role}-submit`}
            >
              {loading ? <div className="spinner" /> : t.login}
            </Button>
          </form>

          <div className={`mt-6 pt-4 border-t ${isDark ? "border-stone-700" : "border-stone-100"} flex items-center justify-between gap-2`}>
            <div className="flex items-center gap-2">
              <Logo className="h-6 opacity-70" />
              <span className={`text-xs ${isDark ? "text-stone-400" : "text-stone-400"}`}>Giotto Carni</span>
            </div>
            <LoginLangSwitcher isDark={isDark} />
          </div>
        </div>
      </div>
    </div>
  );
};

const LoginLangSwitcher = ({ isDark }) => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center gap-1">
      {[{c: "it", f: "🇮🇹"}, {c: "en", f: "🇬🇧"}, {c: "pl", f: "🇵🇱"}].map(({c, f}) => (
        <button
          key={c}
          onClick={() => setLang(c)}
          className={`text-base px-1.5 py-0.5 rounded ${lang === c ? "bg-[#C5A059]/20 ring-1 ring-[#C5A059]/40" : "opacity-50 hover:opacity-100"}`}
          data-testid={`login-lang-${c}`}
          title={c}
        >{f}</button>
      ))}
    </div>
  );
};

export default LoginChooser;
